import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';

import LoadingSpinner from '../../../components/ui/LoadingSpinner';
import { useGetTransactionsQuery } from '../../../store/api/transactionsApi';
import type { Transaction } from '../../../types/api';
import { formatAmountExact } from '../../../utils/amountFormatter';
import { formatDate } from '../../../utils/dateFormatter';

export default function TransactionsListPage() {
  const { t, i18n } = useTranslation();
  const currentLanguage = (i18n.language || 'ar') as 'ar' | 'en';
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);

  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [userId, setUserId] = useState<string>(queryParams.get('userId') || '');
  const [type, setType] = useState<string>('all');
  const [status, setStatus] = useState<string>('all');
  const [method, setMethod] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  const { data, isLoading, error } = useGetTransactionsQuery({
    page,
    limit,
    userId: userId ? parseInt(userId, 10) : undefined,
    type: type === 'all' ? undefined : (type as Transaction['type']),
    status: status === 'all' ? undefined : (status as Transaction['status']),
    method: method === 'all' ? undefined : (method as 'Syriatel' | 'Sham' | 'Payeer' | 'Cryptomus'),
    sortBy,
    sortOrder,
  });

  const transactions: Transaction[] = data?.data?.transactions || [];
  const pagination = data?.data?.pagination;

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1); // Reset to first page on new search
  };

  const clearFilters = () => {
    setUserId('');
    setType('all');
    setStatus('all');
    setMethod('all');
    setSortBy('createdAt');
    setSortOrder('desc');
    setPage(1);
  };

  const getStatusColor = (transactionStatus: string) => {
    switch (transactionStatus) {
      case 'completed':
        return 'bg-green-500/20 text-green-400 border-green-500/50';
      case 'pending':
        return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/50';
      case 'failed':
        return 'bg-red-500/20 text-red-400 border-red-500/50';
      case 'reversed':
        return 'bg-orange-500/20 text-orange-400 border-orange-500/50';
      default:
        return 'bg-neutral-medium/20 text-neutral-medium border-neutral-medium/50';
    }
  };

  const getTypeColor = (transactionType: string) => {
    switch (transactionType) {
      case 'recharge':
      case 'admin_add':
      case 'referral':
        return 'text-brand-luxury drop-shadow-[0_0_2px_rgba(230,161,87,0.15)]';
      case 'debit':
      case 'admin_deduct':
        return 'text-red-400';
      case 'refund':
        return 'text-orange-400';
      default:
        return 'text-neutral-light';
    }
  };

  const getTypeSign = (transactionType: string) => {
    switch (transactionType) {
      case 'recharge':
      case 'admin_add':
      case 'referral':
        return '+';
      case 'debit':
      case 'admin_deduct':
        return '-';
      case 'refund':
        return '↩';
      default:
        return '';
    }
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-danger">
          {t('admin.transactions.error.title') || 'Error loading transactions'}
        </p>
      </div>
    );
  }

  const transactionTypes = ['recharge', 'debit', 'referral', 'refund', 'admin_add', 'admin_deduct'];
  const transactionStatuses = ['pending', 'completed', 'failed', 'reversed'];
  const paymentMethods = ['Syriatel', 'Sham', 'Payeer', 'Cryptomus'];

  return (
    <div className="px-2 sm:px-4 lg:px-0">
      {/* Header with animation */}
      <div
        className="mb-6 sm:mb-8"
        style={{
          animation: 'fadeIn 0.5s ease-out',
        }}
      >
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 mb-2">
          <h1 className="text-2xl sm:text-3xl font-heading font-bold text-brand-accent">
            {t('admin.transactions.title') || 'Transactions'}
          </h1>
          {pagination && (
            <div className="bg-brand-accent/20 border border-brand-accent/50 rounded-lg px-3 sm:px-4 py-2 w-fit">
              <span className="text-xs sm:text-sm font-medium text-neutral-medium">
                {t('admin.transactions.total') || 'Total Transactions'}:{' '}
              </span>
              <span className="text-lg sm:text-xl font-heading font-bold text-brand-accent">
                {pagination.total}
              </span>
            </div>
          )}
        </div>
        <p className="text-sm sm:text-base text-neutral-medium">
          {t('admin.transactions.subtitle') || 'Manage all transactions in the system'}
        </p>
      </div>

      {/* Filters with animation */}
      <div
        className="bg-neutral-dark/50 backdrop-blur-lg rounded-lg p-4 sm:p-6 border border-neutral-dark/50 mb-6 transition-all duration-300 hover:border-brand-accent/30"
        style={{
          animation: 'slideUp 0.5s ease-out 0.1s both',
        }}
      >
        <form onSubmit={handleSearch} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* User ID Search */}
            <div
              style={{
                animation: 'fadeIn 0.5s ease-out 0.2s both',
              }}
            >
              <label htmlFor="userId" className="block text-xs sm:text-sm font-medium text-neutral-light mb-2">
                {t('admin.transactions.filters.user') || 'User ID'}
              </label>
              <input
                id="userId"
                type="text"
                inputMode="numeric"
                value={userId}
                onChange={(e) => setUserId(e.target.value)}
                placeholder={t('admin.transactions.filters.userIdPlaceholder') || 'Search by User ID'}
                className="w-full px-3 sm:px-4 py-2 bg-neutral-dark border border-neutral-medium rounded-lg text-neutral-light placeholder-neutral-medium focus:outline-none focus:ring-2 focus:ring-brand-accent focus:border-transparent transition-all duration-200 text-xs sm:text-sm"
              />
            </div>

            {/* Type Filter */}
            <div
              style={{
                animation: 'fadeIn 0.5s ease-out 0.3s both',
              }}
            >
              <label htmlFor="type" className="block text-xs sm:text-sm font-medium text-neutral-light mb-2">
                {t('admin.transactions.filters.type') || 'Type'}
              </label>
              <select
                id="type"
                value={type}
                onChange={(e) => setType(e.target.value)}
                className="w-full px-3 sm:px-4 py-2 bg-neutral-dark border border-neutral-medium rounded-lg text-neutral-light focus:outline-none focus:ring-2 focus:ring-brand-accent focus:border-transparent transition-all duration-200 text-xs sm:text-sm"
              >
                <option value="all">{t('admin.transactions.filters.all') || 'All'}</option>
                {transactionTypes.map((type) => (
                  <option key={type} value={type}>
                    {t(`admin.transactions.types.${type}`) || type}
                  </option>
                ))}
              </select>
            </div>

            {/* Status Filter */}
            <div
              style={{
                animation: 'fadeIn 0.5s ease-out 0.4s both',
              }}
            >
              <label htmlFor="status" className="block text-xs sm:text-sm font-medium text-neutral-light mb-2">
                {t('admin.transactions.filters.status') || 'Status'}
              </label>
              <select
                id="status"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="w-full px-3 sm:px-4 py-2 bg-neutral-dark border border-neutral-medium rounded-lg text-neutral-light focus:outline-none focus:ring-2 focus:ring-brand-accent focus:border-transparent transition-all duration-200 text-xs sm:text-sm"
              >
                <option value="all">{t('admin.transactions.filters.all') || 'All'}</option>
                {transactionStatuses.map((s) => (
                  <option key={s} value={s}>
                    {t(`admin.transactions.status.${s}`) || s}
                  </option>
                ))}
              </select>
            </div>

            {/* Payment Method Filter */}
            <div
              style={{
                animation: 'fadeIn 0.5s ease-out 0.5s both',
              }}
            >
              <label htmlFor="method" className="block text-xs sm:text-sm font-medium text-neutral-light mb-2">
                {t('admin.transactions.filters.method') || 'Payment Method'}
              </label>
              <select
                id="method"
                value={method}
                onChange={(e) => setMethod(e.target.value)}
                className="w-full px-3 sm:px-4 py-2 bg-neutral-dark border border-neutral-medium rounded-lg text-neutral-light focus:outline-none focus:ring-2 focus:ring-brand-accent focus:border-transparent transition-all duration-200 text-xs sm:text-sm"
              >
                <option value="all">{t('admin.transactions.filters.all') || 'All'}</option>
                {paymentMethods.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>
            </div>

            {/* Sort By */}
            <div
              style={{
                animation: 'fadeIn 0.5s ease-out 0.6s both',
              }}
            >
              <label htmlFor="sortBy" className="block text-xs sm:text-sm font-medium text-neutral-light mb-2">
                {t('admin.transactions.filters.sortBy') || 'Sort By'}
              </label>
              <select
                id="sortBy"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="w-full px-3 sm:px-4 py-2 bg-neutral-dark border border-neutral-medium rounded-lg text-neutral-light focus:outline-none focus:ring-2 focus:ring-brand-accent focus:border-transparent transition-all duration-200 text-xs sm:text-sm"
              >
                <option value="createdAt">{t('admin.transactions.filters.createdAt') || 'Created Date'}</option>
                <option value="amount">{t('admin.transactions.filters.amount') || 'Amount'}</option>
              </select>
            </div>

            {/* Sort Order */}
            <div
              style={{
                animation: 'fadeIn 0.5s ease-out 0.7s both',
              }}
            >
              <label htmlFor="sortOrder" className="block text-xs sm:text-sm font-medium text-neutral-light mb-2">
                {t('admin.transactions.filters.sortOrder') || 'Sort Order'}
              </label>
              <select
                id="sortOrder"
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value as 'asc' | 'desc')}
                className="w-full px-3 sm:px-4 py-2 bg-neutral-dark border border-neutral-medium rounded-lg text-neutral-light focus:outline-none focus:ring-2 focus:ring-brand-accent focus:border-transparent transition-all duration-200 text-xs sm:text-sm"
              >
                <option value="desc">{t('admin.transactions.filters.descending') || 'Descending'}</option>
                <option value="asc">{t('admin.transactions.filters.ascending') || 'Ascending'}</option>
              </select>
            </div>
          </div>

          <div
            className="flex flex-col sm:flex-row gap-3 mt-4"
            style={{
              animation: 'fadeIn 0.5s ease-out 0.8s both',
            }}
          >
            <button
              type="submit"
              className="w-full sm:w-auto px-6 py-2 bg-brand-accent text-brand-primary font-heading font-semibold rounded-lg hover:bg-brand-accent/90 transition-all duration-200 hover:scale-105 active:scale-95 text-sm"
            >
              {t('common.search') || 'Search'}
            </button>
            <button
              type="button"
              onClick={clearFilters}
              className="w-full sm:w-auto px-6 py-2 bg-neutral-medium text-neutral-light font-heading font-semibold rounded-lg hover:bg-neutral-medium/80 transition-all duration-200 hover:scale-105 active:scale-95 text-sm"
            >
              {t('admin.transactions.filters.clear') || 'Clear Filters'}
            </button>
          </div>
        </form>
      </div>

      {/* Transactions Table (Desktop) */}
      <div
        className="hidden lg:block bg-neutral-dark/50 backdrop-blur-lg rounded-lg border border-neutral-dark/50 overflow-hidden transition-all duration-300 hover:border-brand-accent/30"
        style={{
          animation: 'slideUp 0.5s ease-out 0.2s both',
        }}
      >
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-neutral-dark/80 border-b border-neutral-medium">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-heading font-semibold text-neutral-light uppercase tracking-wider">
                  {t('admin.transactions.table.transactionId') || 'Transaction ID'}
                </th>
                <th className="px-6 py-4 text-left text-sm font-heading font-semibold text-neutral-light uppercase tracking-wider">
                  {t('admin.transactions.table.user') || 'User'}
                </th>
                <th className="px-6 py-4 text-left text-sm font-heading font-semibold text-neutral-light uppercase tracking-wider">
                  {t('admin.transactions.table.type') || 'Type'}
                </th>
                <th className="px-6 py-4 text-left text-sm font-heading font-semibold text-neutral-light uppercase tracking-wider">
                  {t('admin.transactions.table.amount') || 'Amount'}
                </th>
                <th className="px-6 py-4 text-left text-sm font-heading font-semibold text-neutral-light uppercase tracking-wider">
                  {t('admin.transactions.table.status') || 'Status'}
                </th>
                <th className="px-6 py-4 text-left text-sm font-heading font-semibold text-neutral-light uppercase tracking-wider">
                  {t('admin.transactions.table.method') || 'Method'}
                </th>
                <th className="px-6 py-4 text-left text-sm font-heading font-semibold text-neutral-light uppercase tracking-wider">
                  {t('admin.transactions.table.created') || 'Created'}
                </th>
                <th className="px-6 py-4 text-left text-sm font-heading font-semibold text-neutral-light uppercase tracking-wider">
                  {t('admin.transactions.table.actions') || 'Actions'}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-medium">
              {transactions.map((transaction, index) => (
                <tr
                  key={transaction._id}
                  className="hover:bg-neutral-dark/50 transition-all duration-200 hover:shadow-lg hover:shadow-brand-accent/5"
                  style={{
                    animation: `fadeIn 0.4s ease-out ${index * 30}ms both`,
                  }}
                >
                  <td className="px-6 py-4 whitespace-nowrap text-neutral-light text-sm">
                    <span className="font-mono">{transaction._id.slice(-8)}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-neutral-light text-sm">
                    <Link
                      to={`/admin/users/${transaction.userId}`}
                      className="text-brand-accent hover:text-brand-accent/80 font-semibold transition-colors duration-200 hover:underline"
                    >
                      {transaction.userId}
                    </Link>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <span className={`capitalize ${getTypeColor(transaction.type)}`}>
                      {getTypeSign(transaction.type)}{' '}
                      {t(`admin.transactions.types.${transaction.type}`) || transaction.type}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <span className={`font-semibold ${getTypeColor(transaction.type)}`}>
                      {getTypeSign(transaction.type)}${formatAmountExact(transaction.amount)} {transaction.currency}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`px-2 py-1 text-xs font-semibold rounded border ${getStatusColor(transaction.status)} transition-transform duration-200 hover:scale-105`}
                    >
                      {t(`admin.transactions.status.${transaction.status}`) || transaction.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-neutral-medium text-sm">
                    {transaction.method || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-neutral-medium text-sm">
                    {formatDate(transaction.createdAt, 'PP', currentLanguage)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <Link
                      to={`/admin/transactions/${transaction._id}`}
                      className="text-brand-accent hover:text-brand-accent/80 font-semibold transition-all duration-200 hover:underline inline-flex items-center gap-1"
                    >
                      {t('admin.transactions.table.view') || 'View'}
                      <svg
                        className="w-4 h-4 inline-block transition-transform duration-200 group-hover:translate-x-1"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Transactions Cards (Mobile) */}
      <div className="lg:hidden grid grid-cols-1 sm:grid-cols-2 gap-4">
        {transactions.map((transaction, index) => (
          <div
            key={transaction._id}
            className="bg-neutral-dark/50 backdrop-blur-lg rounded-lg p-4 border border-neutral-dark/50 transition-all duration-200 hover:border-brand-accent/50 hover:shadow-lg hover:shadow-brand-accent/5 animate-slide-up"
            style={{ animationDelay: `${index * 50}ms` }}
          >
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="text-lg font-heading font-bold text-neutral-light">
                  {t(`admin.transactions.types.${transaction.type}`) || transaction.type}
                </h3>
                <p className="text-xs text-neutral-medium font-mono mt-1">
                  {transaction._id.slice(-8)}
                </p>
              </div>
              <Link
                to={`/admin/transactions/${transaction._id}`}
                className="text-brand-accent hover:text-brand-accent/80 font-semibold transition-all duration-200 hover:underline inline-flex items-center gap-1 text-sm"
              >
                {t('admin.transactions.table.view') || 'View'}
                <svg
                  className="w-4 h-4 inline-block transition-transform duration-200 group-hover:translate-x-1"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </Link>
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2">
                <span className="text-neutral-medium font-medium">
                  {t('admin.transactions.table.user') || 'User'}:
                </span>
                <Link
                  to={`/admin/users/${transaction.userId}`}
                  className="text-brand-accent hover:text-brand-accent/80 font-semibold transition-colors duration-200 hover:underline"
                >
                  {transaction.userId}
                </Link>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-neutral-medium font-medium">
                  {t('admin.transactions.table.amount') || 'Amount'}:
                </span>
                <span className={`font-semibold ${getTypeColor(transaction.type)}`}>
                  {getTypeSign(transaction.type)}${formatAmountExact(transaction.amount)} {transaction.currency}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-neutral-medium font-medium">
                  {t('admin.transactions.table.status') || 'Status'}:
                </span>
                <span
                  className={`px-2 py-1 text-xs font-semibold rounded border ${getStatusColor(transaction.status)}`}
                >
                  {t(`admin.transactions.status.${transaction.status}`) || transaction.status}
                </span>
              </div>
              {transaction.method && (
                <div className="flex items-center gap-2">
                  <span className="text-neutral-medium font-medium">
                    {t('admin.transactions.table.method') || 'Method'}:
                  </span>
                  <span className="text-neutral-light">{transaction.method}</span>
                </div>
              )}
              <div className="flex items-center gap-2">
                <span className="text-neutral-medium font-medium">
                  {t('admin.transactions.table.created') || 'Created'}:
                </span>
                <span className="text-neutral-light text-xs">
                  {formatDate(transaction.createdAt, 'PP', currentLanguage)}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Pagination with animation */}
      {pagination && pagination.pages > 1 && (
        <div
          className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-3"
          style={{
            animation: 'fadeIn 0.5s ease-out 0.4s both',
          }}
        >
          <div className="text-neutral-medium text-xs sm:text-sm text-center sm:text-left">
            {t('admin.transactions.pagination.showing') || 'Showing'}{' '}
            <span className="text-brand-accent font-semibold">
              {(pagination.page - 1) * pagination.limit + 1}
            </span>{' '}
            -{' '}
            <span className="text-brand-accent font-semibold">
              {Math.min(pagination.page * pagination.limit, pagination.total)}
            </span>{' '}
            {t('admin.transactions.pagination.of') || 'of'}{' '}
            <span className="text-brand-accent font-semibold">{pagination.total}</span>{' '}
            {t('admin.transactions.pagination.transactions') || 'transactions'}
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={pagination.page === 1}
              className="px-3 sm:px-4 py-2 bg-neutral-dark border border-neutral-medium rounded-lg text-neutral-light hover:bg-neutral-medium/50 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed hover:scale-105 active:scale-95 disabled:hover:scale-100 text-xs sm:text-sm"
            >
              {t('admin.transactions.pagination.previous') || 'Previous'}
            </button>
            <span className="px-3 sm:px-4 py-2 bg-neutral-dark border border-neutral-medium rounded-lg text-neutral-light text-xs sm:text-sm hidden sm:block">
              {t('admin.transactions.pagination.page') || 'Page'}{' '}
              <span className="text-brand-accent font-semibold">{pagination.page}</span>{' '}
              {t('admin.transactions.pagination.of') || 'of'}{' '}
              <span className="text-brand-accent font-semibold">{pagination.pages}</span>
            </span>
            <button
              onClick={() => setPage((p) => Math.min(pagination.pages, p + 1))}
              disabled={pagination.page === pagination.pages}
              className="px-3 sm:px-4 py-2 bg-neutral-dark border border-neutral-medium rounded-lg text-neutral-light hover:bg-neutral-medium/50 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed hover:scale-105 active:scale-95 disabled:hover:scale-100 text-xs sm:text-sm"
            >
              {t('admin.transactions.pagination.next') || 'Next'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
